'use client';

import { useEffect } from 'react';

/**
 * Handles HMR-related fetch errors that occur during Next.js hot module replacement.
 * These errors are non-critical and happen when the router tries to prefetch data
 * during HMR but the fetch fails (often because the server isn't ready).
 * 
 * This component suppresses these console errors to reduce noise during development.
 */
export function HmrErrorHandler() {
  useEffect(() => {
    // Handle unhandled promise rejections (like failed fetches during HMR)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      // Check if this is a fetch error
      const isFetchError = 
        error?.message === 'Failed to fetch' ||
        (error?.name === 'TypeError' && error?.message?.includes('fetch')) ||
        (typeof error === 'string' && error.includes('Failed to fetch'));
      
      if (!isFetchError) {
        // Not a fetch error, let it through
        return;
      }
      
      // Check if this is happening during HMR (stack trace contains HMR-related code)
      const stack = error?.stack || '';
      const isHmrContext = 
        stack.includes('hmrRefresh') ||
        stack.includes('fetchMissingDynamicData') ||
        stack.includes('navigateToSeededRoute') ||
        stack.includes('refreshDynamicData') ||
        stack.includes('spawnDynamicRequests') ||
        stack.includes('fetchServerResponse');
      
      // Suppress HMR-related fetch errors
      if (isHmrContext) {
        event.preventDefault(); // Prevent the error from being logged to console
        // Silently handle the error - it's just HMR trying to prefetch data
        return;
      }
      
      // For other fetch errors, let them through normally
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}
